import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { MemberActivity } from '@ready/shared';
import { formatDate } from '@/lib/dates';
import { Button, Select } from '@/components/ui';

/*
 * Pieces shared by people lists: an org's members and platform admins' users.
 */

export const ACTIVITY: Record<MemberActivity, { label: string; hint: string; className: string }> =
  {
    active: {
      label: 'Active',
      hint: 'Learned here in the last 30 days',
      className: 'border-india-green/40 bg-india-green/10 text-india-green',
    },
    quiet: {
      label: 'Quiet',
      hint: 'Last active 30 to 90 days ago',
      className: 'border-primary/40 bg-primary/10 text-primary-strong',
    },
    inactive: {
      label: 'Inactive',
      hint: 'Nothing in the last 90 days, or not started',
      className: 'border-border bg-muted text-muted-foreground',
    },
  };

/** Activity filter options: "Active: learned here in the last 30 days" and so on. */
export const ACTIVITY_OPTIONS = (Object.keys(ACTIVITY) as MemberActivity[]).map(
  (a) => [a, `${ACTIVITY[a].label}: ${ACTIVITY[a].hint.toLowerCase()}`] as [string, string],
);

export function ActivityBadge({ activity }: { activity: MemberActivity }) {
  const a = ACTIVITY[activity];
  return (
    <span
      title={a.hint}
      className={`inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 text-xs font-medium ${a.className}`}
    >
      {a.label}
    </span>
  );
}

export const lastActiveLabel = (at: string | null) =>
  at ? `Last active ${formatDate(at)}` : 'No activity yet';

/** "2 of 4", or "—" when nothing was started. */
export const ofTotal = (done: number, started: number) => (started ? `${done} of ${started}` : '—');

export function Filter({
  id,
  label,
  value,
  onChange,
  options,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: [string, string][];
}) {
  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-sm font-medium">
        {label}
      </label>
      <Select id={id} value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </Select>
    </div>
  );
}

/** "1–25 of 222" with Previous and Next. */
export function Pager({
  page,
  pageSize,
  total,
  busy,
  onPage,
}: {
  page: number;
  pageSize: number;
  total: number;
  busy: boolean;
  onPage: (page: number) => void;
}) {
  if (!total) return null;
  const from = page * pageSize + 1;
  const to = Math.min(total, (page + 1) * pageSize);
  return (
    <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
      <p className="text-sm text-muted-foreground" aria-live="polite">
        {from.toLocaleString('en-US')}–{to.toLocaleString('en-US')} of{' '}
        {total.toLocaleString('en-US')}
      </p>
      <div className="flex items-center gap-2">
        <Button variant="secondary" disabled={page === 0 || busy} onClick={() => onPage(page - 1)}>
          <ChevronLeft className="size-4" aria-hidden /> Previous
        </Button>
        <Button variant="secondary" disabled={to >= total || busy} onClick={() => onPage(page + 1)}>
          Next <ChevronRight className="size-4" aria-hidden />
        </Button>
      </div>
    </div>
  );
}
